/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";
import { toast } from "react-toastify";
import axios from "axios";

const ManagePrompts = () => {
  const [firstResponsePrompts, setFirstResponsePrompts] = useState([]);
  const [tellMeMorePrompts, setTellMeMorePrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptContent, setPromptContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/admin/python-prompts");
      const data = response?.data || {};
      
      // Handle new API structure with two categories
      const firstResponse = data.first_response_prompts || [];
      const tellMeMore = data.tell_me_more_prompts || [];
      
      setFirstResponsePrompts(firstResponse);
      setTellMeMorePrompts(tellMeMore);
      
      // Auto-select first prompt if available
      const allPrompts = [...firstResponse, ...tellMeMore];
      if (allPrompts.length > 0 && !selectedPrompt) {
        loadPromptContent(allPrompts[0]);
      }
    } catch (error) {
      toast.error("Failed to load prompts from Python API");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadPromptContent = async (filename) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/admin/python-prompts/${filename}`);
      setSelectedPrompt(filename);
      setPromptContent(response?.data?.content || "");
    } catch (error) {
      toast.error(`Failed to load prompt: ${filename}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPrompt) {
      toast.error("No prompt selected");
      return;
    }

    if (!promptContent.trim()) {
      toast.error("Prompt content cannot be empty");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`/api/admin/python-prompts/${selectedPrompt}`, {
        content: promptContent
      });
      toast.success("Prompt updated successfully!");
      fetchPrompts();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update prompt");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="flex w-full justify-between items-center p-3 flex-wrap md:flex-nowrap">
        <h2 className="font-semibold text-3xl text-left rtl:text-right text-gray-900">
          Manage Prompts
        </h2>
      </div>

      <div className="p-6 bg-white">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar - Prompt List */}
	          <div className="col-span-12 md:col-span-4 lg:col-span-3">
            <div className="col-span-12 md:col-span-8 lg:col-span-9">
              {/* First Response Prompts Section */}
              {firstResponsePrompts.length > 0 && (
                <div>
                  <h3 className="text-md font-semibold text-gray-700 mb-2 px-2">
                    First Response Prompts ({firstResponsePrompts.length})
                  </h3>
                  <div className="space-y-2">
                    {firstResponsePrompts.map((filename) => (
                      <div
                        key={filename}
                        onClick={() => loadPromptContent(filename)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedPrompt === filename
                            ? "bg-green-100 border-2 border-green-500"
                            : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tell Me More Prompts Section */}
              {tellMeMorePrompts.length > 0 && (
                <div>
                  <h3 className="text-md font-semibold text-gray-700 mb-2 px-2">
                    Tell Me More Prompts ({tellMeMorePrompts.length})
                  </h3>
                  <div className="space-y-2">
                    {tellMeMorePrompts.map((filename) => (
                      <div
                        key={filename}
                        onClick={() => loadPromptContent(filename)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedPrompt === filename
                            ? "bg-blue-100 border-2 border-blue-500"
                            : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {filename}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {firstResponsePrompts.length === 0 && tellMeMorePrompts.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">No prompts available</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Prompt Editor */}
	          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            {selectedPrompt ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Editing: {selectedPrompt}
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>

                <textarea
                  value={promptContent}
                  onChange={(e) => setPromptContent(e.target.value)}
                  placeholder="Enter the system prompt content..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  rows="25"
                  style={{ minHeight: "600px" }}
                />

                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Statistics:</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Characters:</span> {promptContent.length}
                    </div>
                    <div>
                      <span className="font-medium">Words:</span> {promptContent.split(/\s+/).filter(w => w).length}
                    </div>
                    <div>
                      <span className="font-medium">Lines:</span> {promptContent.split('\n').length}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-96 text-gray-400">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm">Select a prompt from the list to edit</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <LoadingOverlay />}
    </div>
  );
};

export default ManagePrompts;
